import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams, Routes, Route } from 'react-router-dom'
import { SearchBar } from './components/SearchBar'
import { SearchResults } from './components/SearchResults'
import { WordDefinition } from './types/dictionary'
import { SearchResult } from './types/search'
import { searchWords } from './lib/search'
import { getWordsByTag } from './lib/tagSearch'
import { loadDefinitionForWord } from './lib/data/loader'
import stats from './data/stats.json'

function AppContent() {
  const [results, setResults] = useState<SearchResult[]>([]);
  const [definitions, setDefinitions] = useState<Map<string, WordDefinition>>(new Map());
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const navigate = useNavigate();
  const params = useParams();
  
  // Track the current active query to prevent race conditions
  const currentQueryRef = useRef<string>('');

  // Get query from params
  const urlQuery = params.query;
  const urlWord = params.word;
  const urlTag = params.tag;

  // Handle URL parameter - load from URL on mount or when it changes
  useEffect(() => {
    if (urlQuery) {
      performSearch(decodeURIComponent(urlQuery));
    } else if (urlWord) {
      performSearch(decodeURIComponent(urlWord));
    } else if (urlTag) {
      performTagSearch(decodeURIComponent(urlTag));
    }
  }, [urlQuery, urlWord, urlTag]);

  // Perform tag search
  const performTagSearch = async (tag: string) => {
    currentQueryRef.current = `tag:${tag}`;
    setIsLoading(true);
    setIsLoadingMore(false);
    
    try {
      const tagResults = await getWordsByTag(tag);
      
      if (currentQueryRef.current !== `tag:${tag}`) return;
      
      // Smart Redirect: If no words found for this tag, check if the tag ITSELF is a word
      if (tagResults.length === 0) {
        // Search with higher maxResults to ensure we find the exact match even if it's not the highest frequency word
        const searchResults = await searchWords(tag, { maxResults: 20 });
        
        // Find exact match (case-insensitive)
        const exactMatch = searchResults.find(r => r.word.word.toLowerCase() === tag.toLowerCase());
        
        if (exactMatch) {
           // It's a word! Redirect to definition page
           navigate(`/def/${encodeURIComponent(exactMatch.word.word)}`, { replace: true });
           return;
        }
      }

      setResults(tagResults);
      // Note: We do NOT auto-load definitions for tag view to avoid massive data fetching.
      // Users will click individual words to load their definitions.
    } catch (error) {
      console.error('Tag search error:', error);
    } finally {
      if (currentQueryRef.current === `tag:${tag}`) {
        setIsLoading(false);
      }
    }
  };

  // Perform the actual search
  const performSearch = async (query: string) => {
    // Update active query ref immediately
    currentQueryRef.current = query;
    
    if (!query.trim()) {
      setResults([]);
      setIsLoadingMore(false);
      return;
    }

    setIsLoading(true);
    setIsLoadingMore(false);

    try {
      const searchResults = await searchWords(query, {
        maxResults: 10,
        // Progressive callback for single-character searches
        onProgressiveResults: query.length === 1 ? (progressiveResults) => {
          // Check if this result is still relevant for the current query
          if (currentQueryRef.current !== query) {
            return;
          }
          
          // Update results as more data loads (deduplication happens in search engine)
          setResults(progressiveResults);
          setIsLoadingMore(true);
          // Don't load definitions during progressive updates - wait for final results
        } : undefined,
      });
      
      // Check if the query has changed while we were searching
      if (currentQueryRef.current !== query) {
        return;
      }

      setResults(searchResults);

      // Auto-load definitions for all results
      for (const result of searchResults) {
        // Double check query validity inside the loop if it takes time
        if (currentQueryRef.current !== query) break;
        
        const word = result.word.word;
        if (!definitions.has(word)) {
          const definition = await loadDefinitionForWord(word);
          if (definition) {
            setDefinitions(prev => new Map(prev).set(word, definition));
          }
        }
      }

      // Set loading more indicator for single-character searches
      if (query.length === 1 && currentQueryRef.current === query) {
        setIsLoadingMore(true);
        // Clear the "loading more" indicator after 10 seconds (progressive loading should complete by then)
        setTimeout(() => {
          if (currentQueryRef.current === query) {
            setIsLoadingMore(false);
          }
        }, 10000);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      // Only turn off loading if we're still on the same query
      if (currentQueryRef.current === query) {
        setIsLoading(false);
      }
    }
  };

  // Handle search from search bar - navigate to /head/:query
  const handleSearch = useCallback(async (query: string) => {
    if (!query.trim()) {
      setResults([]);
      navigate('/');
      return;
    }

    // Navigate to /head/:query for typing/searching
    navigate(`/head/${encodeURIComponent(query)}`, { replace: true });
  }, [navigate]);

  // Handle selecting a specific word - navigate to /def/:word
  const handleSelectWord = useCallback(async (word: string) => {
    navigate(`/def/${encodeURIComponent(word)}`);
  }, [navigate]);

  // Load definition for a word
  const handleLoadDefinition = useCallback(async (word: string) => {
    if (definitions.has(word)) {
      // If already loaded, just navigate to it
      handleSelectWord(word);
      return;
    }

    try {
      const definition = await loadDefinitionForWord(word);
      if (definition) {
        setDefinitions(prev => new Map(prev).set(word, definition));
        handleSelectWord(word);
      }
    } catch (error) {
      console.error('Failed to load definition:', error);
    }
  }, [definitions, handleSelectWord]);

  // Handle tag click - search for the related term
  const handleTagClick = useCallback((term: string) => {
    navigate(`/tag/${encodeURIComponent(term)}`);
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4 sm:p-8">
      <div className="max-w-4xl mx-auto">
        <header className="mb-8 text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-slate-900 to-slate-700 dark:from-slate-100 dark:to-slate-400 mb-2 tracking-tight">
            Keio Dictionary
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400 font-serif italic mb-4">
            Humanities-Focused Dictionary with Character-Position Search
          </p>
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-slate-200/50 dark:bg-slate-700/50 text-slate-600 dark:text-slate-300 text-sm font-medium backdrop-blur-sm">
            <span>📚 {stats.totalWords.toLocaleString()} Words Indexed</span>
          </div>
        </header>

        {/* Search Bar */}
        <div className="mb-8">
          <SearchBar
            onSearch={handleSearch}
            placeholder="Search by any character position..."
            initialValue={urlQuery ? decodeURIComponent(urlQuery) : ''}
          />
        </div>

        {/* Search Results */}
        <main>
          <SearchResults
            results={results}
            definitions={definitions}
            onLoadDefinition={handleLoadDefinition}
            onTagClick={handleTagClick}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
          />
        </main>

        <footer className="mt-16 text-center text-xs text-slate-400">
           <p>Phase 1 PoC: Character-Position-Aware Search Engine</p>
           <p className="mt-2">Keio University Literature Department Exam Dictionary</p>
        </footer>
      </div>
    </div>
  )
}

function App() {
  return (
    <Routes>
      <Route path="/" element={<AppContent />} />
      <Route path="/head/:query" element={<AppContent />} />
      <Route path="/def/:word" element={<AppContent />} />
      <Route path="/tag/:tag" element={<AppContent />} />
    </Routes>
  )
}

export default App
